import { gql } from "@apollo/client";
import Fragments from "./fragments";

const { MEDIA_PLAYBACK_FIELDS, CORE_SEARCH_FIELD_METADATA } = Fragments;

const SEARCH = gql`
    query Search(
        $query: String!
        $page: Int
        $limit: Int
        $feedPreference: FeedPreference
        $languagePreference: LanguagePreference
        $contentPreference: ContentPreference
        $forgeInstance: ForgeType = MLB
        $queryType: QueryType = STRUCTURED
    ) {
        search(
            query: $query
            limit: $limit
            page: $page
            feedPreference: $feedPreference
            languagePreference: $languagePreference
            contentPreference: $contentPreference
            forgeInstance: $forgeInstance
            queryType: $queryType
        ) {
            plays {
                mediaPlayback {
                    id
                    slug
                    blurb
                    date
                    description
                    title
                    canAddToReel
                    feeds {
                        type
                        duration
                        image {
                            altText
                            templateUrl
                            cuts {
                                width
                                src
                            }
                        }
                        playbacks {
                            name
                        }
                    }
                    playInfo {
                        balls
                        strikes
                        outs
                        inning
                        inningHalf
                        pitchSpeed
                        pitchType
                        exitVelocity
                        hitDistance
                        launchAngle
                        spinRate
                        scoreDifferential
                        gamePk
                        runners {
                            first
                            second
                            third
                        }
                        teams {
                            away {
                                name
                                shortName
                                triCode
                            }
                            home {
                                name
                                shortName
                                triCode
                            }
                            batting {
                                name
                                shortName
                                triCode
                            }
                            pitching {
                                name
                                shortName
                                triCode
                            }
                        }
                        players {
                            pitcher {
                                id
                                name
                                lastName
                                playerHand
                            }
                            batter {
                                id
                                name
                                lastName
                                playerHand
                            }
                        }
                    }
                    isInReel @client
                    keywordsDisplay {
                        slug
                        displayName
                    }
                }
            }
            total
        }
    }
`;

const SEARCH_WITH_PLAYBACK = gql`
    query Search(
        $query: String!
        $page: Int
        $limit: Int
        $feedPreference: FeedPreference
        $languagePreference: LanguagePreference
        $contentPreference: ContentPreference
        $forgeInstance: ForgeType = MLB
        $queryType: QueryType = STRUCTURED
        $withPlaybacksSegments: Boolean = false
    ) {
        search(
            query: $query
            limit: $limit
            page: $page
            feedPreference: $feedPreference
            languagePreference: $languagePreference
            contentPreference: $contentPreference
            forgeInstance: $forgeInstance
            queryType: $queryType
        ) {
            plays {
                mediaPlayback {
                    ...MediaPlaybackFields
                }
            }
            total
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const SEARCH_GAMES = gql`
    query Search(
        $query: String!
        $page: Int
        $limit: Int
        $feedPreference: FeedPreference
        $languagePreference: LanguagePreference
        $contentPreference: ContentPreference
        $forgeInstance: ForgeType = MLB
        $queryType: QueryType = STRUCTURED
    ) {
        search(
            query: $query
            limit: $limit
            page: $page
            feedPreference: $feedPreference
            languagePreference: $languagePreference
            contentPreference: $contentPreference
            forgeInstance: $forgeInstance
            queryType: $queryType
        ) {
            plays {
                gamePk
                gameDate
                mediaPlayback {
                    playInfo {
                        teams {
                            away {
                                id
                            }
                            home {
                                id
                            }
                        }
                    }
                }
            }
            total
        }
    }
`;

const GET_QUERY_INFO = gql`
    query getQueryInfo(
        $queries: [String]!
        $queryType: QueryType = STRUCTURED
        $includeCount: Boolean = false
    ) {
        queryInfo(queries: $queries, queryType: $queryType) {
            query
            valid
            graph
            count @include(if: $includeCount)
        }
    }
`;

const GET_FIELDS = gql`
    query getFields($ids: [Int], $idsSkipValues: [Int]) {
        withValues: fields(ids: $ids) {
            ...CoreSearchFieldMetadata
            values {
                value
                displayValue
            }
        }
        withoutValues: fields(ids: $idsSkipValues) {
            ...CoreSearchFieldMetadata
        }
    }
    ${CORE_SEARCH_FIELD_METADATA}
`;

const GET_CLIP = gql`
    query clipQuery(
        $ids: [String]
        $languagePreference: LanguagePreference
        $idType: MediaPlaybackIdType
        $forgeInstance: ForgeType = MLB
        $userId: String!
        $withUser: Boolean!
        $withPlaybacksSegments: Boolean = false
    ) {
        mediaPlayback(
            ids: $ids
            languagePreference: $languagePreference
            idType: $idType
            forgeInstance: $forgeInstance
        ) {
            ...MediaPlaybackFields
        }
        userInfo(userId: $userId) @include(if: $withUser) {
            firstName
            nickName
            userId
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_TOPIC = gql`
    query getTopic(
        $topicId: String!
        $languagePreference: LanguagePreference
        $limit: Int
        $page: Int
        $videoSlug: String
        $contentGroup: ContentGroup
        $videoTranslationId: String
        $sessionId: String
        $forgeInstance: ForgeType = MLB
        $withPlaybacksSegments: Boolean = false
    ) {
        topicPlayList(
            topicId: $topicId
            languagePreference: $languagePreference
            limit: $limit
            page: $page
            videoSlug: $videoSlug
            contentGroup: $contentGroup
            videoTranslationId: $videoTranslationId
            sessionId: $sessionId
            forgeInstance: $forgeInstance
        ) {
            hasMore
            title
            puid
            modelType
            modelVersion
            mediaPlayback {
                ...MediaPlaybackFields
            }
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_SELECTED_FILTERS = gql`
    query getSelectedFilters {
        selectedFilters @client {
            name
            type
            comparator
            fieldId
            displayName
            logicalConjunctionOperator
            values {
                value
                label
            }
        }
    }
`;

const GET_SEARCH_SORT = gql`
    query getSearchSort {
        searchSort @client
    }
`;

const GET_PAGE = gql`
    query getPage {
        page @client
    }
`;

const GET_MODALS = gql`
    query getModals {
        modals @client
    }
`;

const GET_NAV_ITEMS = gql`
    query getNavItems {
        navItems @client {
            text
            link
        }
    }
`;

const GET_IS_EU = gql`
    query getIsEU {
        isEU @client
    }
`;

const GET_TARGET_OFFER = gql`
    query getTargetOffer {
        targetOffer @client
    }
`;

const GET_REEL = gql`
    query getReel {
        reel @client {
            reelTitle
            videoUrl
            thumbnailUrl
            duration
            reelClips {
                mediaId
                clipSlug
                feedType
                thumbnailUrl
                originalDuration
                offsetStartTime
                offsetEndTime
                trimmedDuration
            }
        }
    }
`;

const GET_SHARE_MEDIA_ID = gql`
    query getShareMediaId {
        shareMediaId @client
    }
`;

const GET_REELS = gql`
    query getReels(
        $userId: String!
        $limit: Int
        $page: Int
        $withPlaybacksSegments: Boolean = false
    ) {
        getReels(userId: $userId, limit: $limit, page: $page) {
            total
            reels {
                id
                mediaPlayback {
                    ...MediaPlaybackFields
                }
            }
        }
        userInfo(userId: $userId) {
            firstName
            nickName
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_REEL_CLIP_TRIMMING_MEDIA_ID = gql`
    query getReelClipTrimmingMediaId {
        trimmingMediaId @client
    }
`;

const VALIDATE_REEL = gql`
    query ValidateReel($reelRequest: ReelInput!) {
        validateReel(reelRequest: $reelRequest) {
            valid
            errors
        }
    }
`;

const GET_USER_DATA = gql`
    query getUserData {
        userData @client(always: true) {
            isLoggedIn
            accessToken
            userId
        }
    }
`;

const GET_USER_LOCATION = gql`
    query getUserLocation(
        $token: String
        $runTimeConfig: String
    ) {
        userLocation(
            token: $token
            runTimeConfig: $runTimeConfig
        ) @client(always: true) {
            metroCode
            postalCode
            country
        }
    }
`;

const GET_USER_SUGGESTIONS = gql`
    query getUserSuggestions(
        $limit: Int
        $languagePreference: LanguagePreference
        $userLocation: UserLocation!
        $experience: String!
        $forgeInstance: ForgeType = MLB
        $userId: String = "anonymous"
        $withPlaybacksSegments: Boolean = false
    ) {
        userSuggestions(
            limit: $limit
            languagePreference: $languagePreference
            userLocation: $userLocation
            experience: $experience
            forgeInstance: $forgeInstance
            userId: $userId
        ) {
            ...MediaPlaybackFields
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_NOTIFICATION = gql`
    query getNotification($id: String!) {
        notification(id: $id) @client(always: true) {
            id
            isDismissed
        }
    }
`;

const GET_DELETE_REEL_ID = gql`
    query getDeleteReelId {
        deleteReelId @client
    }
`;

const GET_CONTENT_TAG_DISPLAY_VALUES = gql`
    query getContentTagDisplayValues(
        $contentTags: [String!]
        $forgeInstance: ForgeType = MLB
    ) {
        contentTagDisplayValues(
            contentTags: $contentTags
            forgeInstance: $forgeInstance
        ) {
            slug
            displayName
        }
    }
`;

const GET_IS_SORTING = gql`
    query getIsSorting {
        isSorting @client
    }
`;

const GET_GAME_STATUS = gql`
    query getGameStatus($ids: [Int]!) {
        games(ids: $ids) {
            status
            gameDate
        }
    }
`;

const GET_GAMES = gql`
    query getGames(
        $ids: [Int]!
        $languagePreference: LanguagePreference
        $withPlaybacksSegments: Boolean = false
    ) {
        games(ids: $ids, languagePreference: $languagePreference) {
            mediaPlayback {
                ...MediaPlaybackFields
            }
            lineScore {
                inning
                inningHalf
                inningState
                outs
                balls
                strikes
                away {
                    runs
                    hits
                    errors
                }
                home {
                    runs
                    hits
                    errors
                }
                innings {
                    num
                    ordinalNum
                    home {
                        runs
                        hits
                        errors
                    }
                    away {
                        runs
                        hits
                        errors
                    }
                }
            }
            venueId
            gameDate
            gamePk
            away {
                id
                name
                shortName
                abbreviation
            }
            home {
                id
                name
                shortName
                abbreviation
            }
            status
            decisions {
                winner {
                    id
                    name
                    playerHand
                    innings
                    earnedRuns
                    strikeouts
                    era
                    wins
                    losses
                    saves
                }
                loser {
                    id
                    name
                    playerHand
                    innings
                    earnedRuns
                    strikeouts
                    era
                    wins
                    losses
                    saves
                }
                save {
                    id
                    name
                    playerHand
                    innings
                    earnedRuns
                    strikeouts
                    era
                    wins
                    losses
                    saves
                }
            }
            image {
                title
                altText
                templateUrl
            }
            headline
            seoTitle
            storyUrl
        }
    }
    ${MEDIA_PLAYBACK_FIELDS}
`;

const GET_SUGGESTED_SEARCHES = gql`
    query getSuggestedSearches($total: Int) {
        suggestedSearches(total: $total)
    }
`;

const GET_CONTAINERS = gql`
    query getContainers($ids: [String]!, $idType: CriterionIdType = NAME) {
        containers(ids: $ids, idType: $idType) {
            containers {
                sections {
                    id
                    type {
                        name
                    }
                    fields {
                        type
                        name
                        value
                    }
                }
            }
        }
    }
`;

const GET_USER_INFO = gql`
    query getUserInfo($userId: String!) {
        userInfo(userId: $userId) {
            followedPlayers {
                id
                lastName
                name
            }
            followedTeams {
                id
                name
                shortName
            }
            favoriteTeam {
                id
                name
                shortName
            }
        }
    }
`;

const GET_SUGGESTED_STORIES = gql`
    query getSuggestedStories(
        $preview: Boolean
        $languagePreference: LanguagePreference
        $search: String!
        $searchType: StoriesSearchType = VSM_SLUG
        $limit: Int
        $skip: Int
    ) {
        suggestedStories(
            preview: $preview
            languagePreference: $languagePreference
            search: $search
            searchType: $searchType
            limit: $limit
            skip: $skip
        ) {
            storyType
            title
            metadata
            image {
                templateUrl
            }
        }
    }
`;

export default {
    SEARCH,
    SEARCH_WITH_PLAYBACK,
    SEARCH_GAMES,
    GET_QUERY_INFO,
    GET_FIELDS,
    GET_SELECTED_FILTERS,
    GET_PAGE,
    GET_MODALS,
    GET_CLIP,
    GET_TOPIC,
    GET_IS_EU,
    GET_NAV_ITEMS,
    GET_TARGET_OFFER,
    GET_REEL,
    GET_REELS,
    GET_REEL_CLIP_TRIMMING_MEDIA_ID,
    GET_SHARE_MEDIA_ID,
    VALIDATE_REEL,
    GET_USER_DATA,
    GET_NOTIFICATION,
    GET_DELETE_REEL_ID,
    GET_SEARCH_SORT,
    GET_CONTENT_TAG_DISPLAY_VALUES,
    GET_IS_SORTING,
    GET_GAME_STATUS,
    GET_GAMES,
    GET_SUGGESTED_SEARCHES,
    GET_CONTAINERS,
    GET_USER_INFO,
    GET_USER_LOCATION,
    GET_USER_SUGGESTIONS,
    GET_SUGGESTED_STORIES,
};
